import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { STORE } from '@/config';
import { useCatalog } from '@/context/CatalogContext';
import { useWishlist } from '@/context/WishlistContext';
import { formatPrice } from '@/utils/format';

/**
 * Price bands, labelled in the store's own currency.
 *
 * These compare raw numbers, and the catalogue is priced in more than one
 * currency — so "under 500" means under 500 of whatever that book is priced in.
 * There is no exchange rate available to do better, and inventing one would
 * misreport prices. Pair a band with the Currency filter below to get a
 * comparison that holds.
 */
export const PRICE_BANDS = [
  { id: 'any', label: 'All prices', test: () => true },
  { id: 'u500', label: `Under ${formatPrice(500, STORE.currency)}`, test: (p) => p.price < 500 },
  {
    id: '5001k',
    label: `${formatPrice(500, STORE.currency)}–${formatPrice(1000, STORE.currency)}`,
    test: (p) => p.price >= 500 && p.price <= 1000,
  },
  { id: 'o1k', label: `Over ${formatPrice(1000, STORE.currency)}`, test: (p) => p.price > 1000 },
];

export const SORTS = [
  { id: 'pop', label: 'Most loved', compare: (a, b) => b.rc - a.rc },
  { id: 'lo', label: 'Price: low → high', compare: (a, b) => a.price - b.price },
  { id: 'hi', label: 'Price: high → low', compare: (a, b) => b.price - a.price },
  { id: 'rate', label: 'Top rated', compare: (a, b) => b.rating - a.rating },
  {
    id: 'new',
    label: 'Recently updated',
    compare: (a, b) => String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? '')),
  },
];

/**
 * The book fields a shopper narrows by. Each is a single-select dropdown built
 * from the values actually present in the catalog, so no filter can ever yield
 * an empty result for a value nobody publishes.
 */
export const BOOK_FILTERS = [
  { id: 'lang', label: 'Language', field: 'language' },
  // Publishers price in their own currency, so narrowing to one is what makes
  // the price bands and the price sorts comparable.
  { id: 'cur', label: 'Currency', field: 'currency' },
  { id: 'board', label: 'Board', field: 'education_board' },
  { id: 'grade', label: 'Age group', field: 'grade_level' },
  { id: 'author', label: 'Author', field: 'author' },
];

/** Values present in the catalog for one book field, sorted and de-duplicated. */
function optionsFor(products, field) {
  // Language is offered as "available in": a book counts for its own language
  // and for every translation it ships, so both sources feed the dropdown.
  const values =
    field === 'language'
      ? products.flatMap((p) => [p.language, ...(p.additional_languages ?? [])])
      : products.map((p) => p[field]);

  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/**
 * Filter/sort state lives in the URL so listings are shareable and the back
 * button works — the one meaningful upgrade over the prototype's local state.
 */
export function useProductFilters(categoryId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products } = useCatalog();
  const wishlist = useWishlist();

  const q = searchParams.get('q') ?? '';
  const band = searchParams.get('band') ?? 'any';
  const sort = searchParams.get('sort') ?? 'pop';
  const minRating = searchParams.get('rating') === '4';
  const savedOnly = searchParams.get('saved') === '1';
  const freeOnly = searchParams.get('free') === '1';
  const lang = searchParams.get('lang') ?? '';
  const board = searchParams.get('board') ?? '';
  const grade = searchParams.get('grade') ?? '';
  const author = searchParams.get('author') ?? '';

  const setParam = (key, value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === 'any' || value === 'pop') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  // Only offer a rating filter when something in the catalog is actually rated.
  const hasRatings = useMemo(() => products.some((p) => p.rc > 0), [products]);

  const bookFilters = useMemo(
    () =>
      BOOK_FILTERS.map((f) => ({ ...f, options: optionsFor(products, f.field) })).filter(
        // A dropdown with one option can't narrow anything.
        (f) => f.options.length > 1,
      ),
    [products],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const bandTest = (PRICE_BANDS.find((b) => b.id === band) ?? PRICE_BANDS[0]).test;
    const compare = (SORTS.find((s) => s.id === sort) ?? SORTS[0]).compare;

    // Mirrors the backend's search fields so results match whichever side filters.
    const matchesSearch = (p) =>
      !needle ||
      [p.name, p.categoryId, p.categoryName, p.author, p.subject, p.education_board]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle)) ||
      (p.tags ?? []).some((t) => t.toLowerCase().includes(needle));

    // "Available in X" means the book's own language or one of its translations.
    const matchesLanguage = (p) =>
      !lang || p.language === lang || (p.additional_languages ?? []).includes(lang);

    return products
      .filter((p) => categoryId === 'all' || p.categoryId === categoryId)
      .filter(matchesSearch)
      .filter(bandTest)
      .filter((p) => !minRating || p.rating >= 4.0)
      .filter((p) => !savedOnly || wishlist.has(p.id))
      .filter((p) => !freeOnly || p.pricing?.is_free || p.price === 0)
      .filter(matchesLanguage)
      .filter((p) => !board || p.education_board === board)
      .filter((p) => !grade || p.grade_level === grade)
      .filter((p) => !author || p.author === author)
      .sort(compare);
  }, [
    products,
    categoryId,
    q,
    band,
    sort,
    minRating,
    savedOnly,
    freeOnly,
    lang,
    board,
    grade,
    author,
    wishlist,
  ]);

  return {
    filtered,
    q,
    band,
    sort,
    minRating,
    savedOnly,
    freeOnly,
    hasRatings,
    bookFilters,
    values: { lang, board, grade, author },
    setSearch: (value) => setParam('q', value),
    setBand: (value) => setParam('band', value),
    setSort: (value) => setParam('sort', value),
    setBookFilter: (id, value) => setParam(id, value),
    toggleRating: () => setParam('rating', minRating ? '' : '4'),
    toggleSaved: () => setParam('saved', savedOnly ? '' : '1'),
    toggleFree: () => setParam('free', freeOnly ? '' : '1'),
  };
}
